import React, { useEffect, useState } from 'react';
import authAxios from '../auth/authAxios';
import { Trophy, Medal, Star } from 'lucide-react';
import { useMsal } from '@azure/msal-react';

interface TeamMember {
  user_id: string;
  display_name: string;
  current_score: number;
  rank: number;
  badges: string[];
}

const rankColors = ['bg-yellow-400', 'bg-gray-400', 'bg-yellow-800'];

const Team = () => {
  const { accounts } = useMsal();
  const user = accounts[0];
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const metalStyles = [
    {
      // Gold: bright center, warm edges
      background:
        'radial-gradient(circle at 30% 30%, #fff6a0, #d4af37 40%, #a57c00 80%, #5e4700 100%)',
      boxShadow: '0 0 8px 2px rgba(212, 175, 55, 0.7)',
    },
    {
      // Silver: cool white center, dark edges
      background:
        'radial-gradient(circle at 30% 30%, #f0f0f0, #c0c0c0 40%, #7a7a7a 80%, #3a3a3a 100%)',
      boxShadow: '0 0 8px 2px rgba(192, 192, 192, 0.7)',
    },
    {
      // Bronze: dark center with warm subtle highlights
      background:
        'radial-gradient(circle at 40% 40%, #a1866f, #4a3b2b 70%, #1f140a 100%)',
      boxShadow: '0 0 8px 2px rgba(75, 55, 35, 0.7)',
    },
    {
      // Pewter: warmer gray with slight brown undertones
      background:
        'radial-gradient(circle at 30% 30%, #b8b2a7, #8c857a 40%, #625950 80%, #3d352f 100%)',
      boxShadow: '0 0 8px 2px rgba(139, 133, 122, 0.7)',
    },
    {
      // Dark Steel: deep gunmetal finish for 5th place
      background:
        'radial-gradient(circle at 30% 30%, #4a4a4a, #2f2f2f 40%, #1a1a1a 80%, #000000 100%)',
      boxShadow: '0 0 8px 2px rgba(74, 74, 74, 0.7)',
    },
    {
      // 6th - Dark Mahogany
      background: 'linear-gradient(135deg, #621800 0%, #3a1a00 49%, #621800 50%, #3a1a00 100%)',
      boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)',
    },
    {
      // 7th - Walnut
      background: 'linear-gradient(135deg, #754c29 0%, #4a2b1a 49%, #754c29 50%, #4a2b1a 100%)',
      boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)',
    },
    {
      // 8th - Cherry
      background: 'linear-gradient(135deg, #9b4e3a 0%, #752e1b 49%, #9b4e3a 50%, #752e1b 100%)',
      boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)',
    },
    {
      // 9th - Oak
      background: 'linear-gradient(135deg, #d4b185 0%, #b68c5a 49%, #d4b185 50%, #b68c5a 100%)',
      boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)',
    },
    {
      // 10th - Pine
      background: 'linear-gradient(135deg, #e8d4b0 0%, #d4b684 49%, #e8d4b0 50%, #d4b684 100%)',
      boxShadow: 'inset 0 0 4px rgba(0,0,0,0.5)',
    }
  ];

  useEffect(() => {
    authAxios.get('/api/team/leaderboard')
      .then(res => setMembers(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="p-6 text-center">Loading leaderboard...</p>;
  if (!members.length) return <p className="p-6 text-center">No leaderboard data found.</p>;

  const maxScore = members[0]?.current_score || 10;

  return (
    <main className="max-w-6xl mx-auto p-6">
      <h2 className="text-3xl font-bold mb-8 text-center text-white">Team Legends</h2>
      <div className="rounded-xl bg-gradient-to-br from-cyan-500/20 via-primary/10 to-transparent border border-primary/30 p-6 flex items-center justify-between gap-4 shadow-lg hover:shadow-xl transition mb-5">
        <div className="flex items-center gap-4">
          <img
              src="/ai-elite.png"
              alt="Elite"
              className="w-16 h-16 object-contain drop-shadow"
            />
          <div>
          <h4 className="text-xl font-semibold text-white mb-1">Innovators</h4>
          <p className="text-white/80 text-sm">
            Celebrating those who push AI boundaries every day. Join this elite circle - your breakthrough moment awaits!
          </p>
          </div>
        </div>
        <div>&nbsp;</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {members.map((member, i) => {
          const isTop3 = i < 3;
          const isCurrentUser = member.user_id === user?.localAccountId;
          const scorePercent = (member.current_score / maxScore) * 100;

          return (
            <div
              key={member.user_id}
              className={`min-w-[300px] p-5 rounded-xl shadow-lg border transition hover:shadow-[0_0_10px_rgba(79,255,222,0.1)] hover:scale-[1.05]
                ${isCurrentUser ? 'border-primary bg-primary/10' : 'border-gray-700 bg-bg-panel'}
              `}
            >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3">
                  <div className="relative flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-lg overflow-hidden">
                    <div className="relative flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-lg overflow-hidden">
                      {/* Base color/metal layer */}
                      <div
                        className="absolute inset-0"
                        style={member.rank <= 9
                          ? metalStyles[member.rank - 1]
                          : metalStyles[9]} // Pine style for rank 10+
                      />

                      {/* Wood grain overlay for ranks 6+ */}
                      {member.rank > 5 && (
                        <div className="absolute inset-0 wood-grain" />
                      )}

                      {/* Trophy icon for all ranks */}
                      <div className="relative z-10">
                        <Trophy className="w-7 h-7 drop-shadow-lg" />
                      </div>
                    </div>
                  </div>
                  <div className="text-md font-semibold text-white">
                    {member.display_name} {isCurrentUser ? '(You)' : ''}
                  </div>
                </div>
                <div className="text-md font-mono font-bold text-primary">
                  {member.current_score >= 8 ? 'Expert'
                    : member.current_score >= 6.667 ? 'Pro'
                      : member.current_score >= 4.5 ? 'Riser'
                        : 'New'}
                </div>
              </div>

              <div className="w-full bg-gray-600 rounded-full h-3 overflow-hidden">
                <div
                  className="h-3 bg-primary transition-all"
                  style={{ width: `${scorePercent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );

};

export default Team;
